/**
 * supportDriverBoost.js — 09/02/2026 (Q2 Lot H user prod)
 *
 * Quand Nadia Cheikh (role-nadia-cheikh, RRH) ou Karim Sefrioui
 * (role-karim-sefrioui, chasseur de tête) est embauché, leur escarcelle
 * apporte 4-5 profils variés au vivier de candidats. C'est un boost
 * one-shot qui matérialise le bénéfice pédagogique attendu (« je
 * m'attendais à recevoir plein de profils »).
 *
 * Règles user prod :
 *   - Nadia : 4-5 profils L1-L3 classiques, mix de spécialités.
 *   - Karim : 4-5 profils L4-L5 (élite) + au moins 2 marqués comme
 *     déjà pris par un concurrent (débauchables via poachDriver).
 *   - **Au moins UN profil de niveau > Tier max actuel de la flotte**
 *     pour donner une vraie progression à l'apprenant (Tier 2 → N3+ ;
 *     Tier 3 → N4+).
 *   - Filtrage : on n'ajoute que des profils ABSENTS du pool actuel.
 *
 * Le pool reste « vivant » : le `tickDriverPool` existant continue de
 * faire entrer/sortir des profils marché. Ce helper boostit juste le
 * point de départ.
 */
import { DRIVERS, LUMERY_BROTHER_IDS } from '../../data/drivers';

const COMPETITORS_FOR_POACH = [
  'TransNord Express',
  'LogistikMéridien',
  'Pyrénées Cargo',
  'Atlantique Routier',
];

function pickRandom(arr, count) {
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

function getMaxFleetTier(state) {
  const fleet = state?.vehicles || [];
  if (fleet.length === 0) return 1;
  return Math.max(...fleet.map(v => v.tier || 1));
}

/**
 * Construit un patch state à fusionner pour ajouter les profils boostés
 * du rôle embauché. Retourne null si le rôle n'est pas concerné ou si
 * aucun profil ajoutable n'a pu être trouvé.
 *
 * @param {string} roleId - id du rôle support tout juste embauché
 * @param {Object} state - état complet courant (lecture seule)
 * @returns {{ driverPool, addedIds, role }} | null
 */
export function buildSupportDriverBoostPatch(roleId, state) {
  if (roleId !== 'role-nadia-cheikh' && roleId !== 'role-karim-sefrioui') return null;
  const pool = state?.driverPool || { availableIds: [], takenBy: {} };
  const currentIds = new Set(pool.availableIds || []);
  // 21/09/2026 — jamais (ré)injecter un visage déjà dans l'effectif.
  const rosterIds = new Set((state?.drivers || []).flatMap(d => [d.id, d.dataId].filter(Boolean)));
  const fleetTier = getMaxFleetTier(state);

  let candidates;
  let isKarim = roleId === 'role-karim-sefrioui';

  if (isKarim) {
    // Karim : profils élite (L4-L5) + chasseur ⇒ certains sont chez les
    // concurrents et nécessiteront un débauchage payant.
    candidates = DRIVERS.filter(d => (d.level || 1) >= 4 && !currentIds.has(d.id) && !rosterIds.has(d.id));
  } else {
    // Nadia : profils classiques L1-L3, mix de spécialités.
    candidates = DRIVERS.filter(d =>
      (d.level || 1) >= 1 && (d.level || 1) <= 3 && !currentIds.has(d.id) && !rosterIds.has(d.id)
    );
  }
  if (candidates.length === 0) return null;

  // 21/09/2026 — retour user prod « on ne croise plus les frères Lumery » :
  // le duo d'élite est GARANTI dans l'escarcelle de Karim (ils ne passent
  // plus par le tirage aléatoire) et jamais marqué « pris par concurrent ».
  const guaranteed = isKarim ? candidates.filter(d => LUMERY_BROTHER_IDS.includes(d.id)) : [];
  const drawable = candidates.filter(d => !guaranteed.includes(d));

  // Sélection : 5 profils max, en assurant la variété (heros + market
  // mélangés). On veut au moins 1 niveau > fleetTier (règle apprenant).
  const aboveTier = drawable.filter(d => (d.level || 1) > fleetTier);
  const aboveTierPick = aboveTier.length > 0 ? pickRandom(aboveTier, 1) : [];
  const rest = drawable.filter(d => !aboveTierPick.some(p => p.id === d.id));
  // Cible : 4 ou 5 profils total → garantis + 1 above tier + le reste.
  const targetTotal = 4 + (Math.random() < 0.5 ? 0 : 1);
  const restCount = Math.max(0, targetTotal - guaranteed.length - aboveTierPick.length);
  const restPick = pickRandom(rest, restCount);
  const picks = [...guaranteed, ...aboveTierPick, ...restPick];
  if (picks.length === 0) return null;

  // Les profils apportés passent EN TÊTE du vivier : la fenêtre visible
  // (computeVisibleSlots) ne garde que les N premiers ids.
  const newAvailableIds = [...picks.map(p => p.id), ...(pool.availableIds || [])];
  const newTakenBy = { ...(pool.takenBy || {}) };

  if (isKarim) {
    // Marque 2 des picks (hors Lumery) comme « pris par concurrent » → débauchables.
    const poachCandidates = picks.filter(p => (p.level || 1) >= 4 && !LUMERY_BROTHER_IDS.includes(p.id));
    const toPoach = pickRandom(poachCandidates, Math.min(2, poachCandidates.length));
    toPoach.forEach(p => {
      const comp = COMPETITORS_FOR_POACH[Math.floor(Math.random() * COMPETITORS_FOR_POACH.length)];
      newTakenBy[p.id] = comp;
    });
  }

  return {
    driverPool: {
      ...pool,
      availableIds: newAvailableIds,
      takenBy: newTakenBy,
      lastTickDay: state?.totalDays || 0,
    },
    addedIds: picks.map(p => p.id),
    addedNames: picks.map(p => p.name),
    lumeryIncluded: guaranteed.length === LUMERY_BROTHER_IDS.length,
    role: roleId === 'role-nadia-cheikh' ? 'nadia' : 'karim',
  };
}

/**
 * Construit le payload Messagerie qui accompagne le boost. Le ton est
 * légèrement différent entre Nadia (chaleureuse) et Karim (technique).
 */
export function buildSupportDriverBoostMessage(boostPatch) {
  if (!boostPatch || boostPatch.addedIds.length === 0) return null;
  const isKarim = boostPatch.role === 'karim';
  const names = boostPatch.addedNames;
  const lines = [];
  if (isKarim) {
    lines.push("Patron,");
    lines.push("");
    lines.push("Comme convenu, j'ouvre mon carnet d'adresses. Voici les profils élite que je peux faire entrer dans votre vivier dès aujourd'hui :");
    lines.push("");
    names.forEach(n => lines.push(`  • **${n}**`));
    lines.push("");
    if (boostPatch.lumeryIncluded) {
      lines.push("🎯 Cerise sur le gâteau : j'ai décroché les **frères Lumery** — Philemon et Thimothée sont libres et prêts à signer. Gardez-les au même dépôt, ils se tirent vers le haut.");
      lines.push("");
    }
    lines.push("⚠️ Certains sont déjà en poste chez un concurrent — un **débauchage** sera nécessaire (cf. action « Débaucher » dans RH). Aucun frais, mais comptez +15 % sur leurs prétentions salariales et 35 % de refus.");
    lines.push("");
    lines.push("Je continuerai à sonder mon réseau chaque semaine pour vous ramener d'autres profils élite.");
    lines.push("");
    lines.push("— Karim Sefrioui");
  } else {
    lines.push("Patron,");
    lines.push("");
    lines.push("Première action : j'ai sourcé un panel de candidats variés. Vous les retrouverez dès maintenant dans l'onglet RH :");
    lines.push("");
    names.forEach(n => lines.push(`  • **${n}**`));
    lines.push("");
    lines.push("J'ai veillé à inclure au moins un profil un cran au-dessus de votre flotte actuelle pour anticiper votre montée en gamme. Le vivier se renouvellera ensuite chaque semaine.");
    lines.push("");
    lines.push("— Nadia Cheikh");
  }
  return {
    kind: 'info',
    sender: isKarim ? 'Karim Sefrioui' : 'Nadia Cheikh',
    senderType: 'support',
    senderRole: isKarim ? 'role-karim-sefrioui' : 'role-nadia-cheikh',
    title: isKarim
      ? `Carnet d'adresses ouvert — ${names.length} profils élite ajoutés`
      : `Vivier renforcé — ${names.length} nouveaux candidats`,
    body: lines.join('\n'),
    ttlDays: 30,
  };
}
